import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import styled from 'styled-components'

import { makeStyles } from '@material-ui/core/styles'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'

import PagCircularIndeterminate from '@/components/pag-loading'

export const StyledAutocomplete = styled(Autocomplete)`
  ${({ theme }) => `
    .overview {
      &:hover .MuiInputBase-root:not(.Mui-focused) {
        background-color: ${theme.palette.grey[200]}
      }

      & .MuiOutlinedInput-root {
        &:not(.Mui-focused) fieldset {
          &.MuiOutlinedInput-notchedOutline {
            border-width: 0;
          }
        }
      }
      & .MuiInput-underline {
        &:not(.Mui-focused):before {
          border-width: 0;
        }
      }
      &.status .MuiInputBase-root:not(.Mui-focused) {
        margin-top: -5px;
        padding-right: 8px;
        background: #E0E0E0;

        & .MuiInputBase-input.MuiAutocomplete-input {
          font-size: 12px;
          text-align: center;
        }
        & .MuiAutocomplete-endAdornment {
          display: none;
        }
      }

      &.status .MuiFormLabel-root.MuiInputLabel-root:not(.Mui-focused) {
        display: none;
      }

      .MuiInputLabel-outlined.MuiInputLabel-shrink {
        transform: translate(12px, -6px) scale(0.75);
      }
    }
    .MuiAutocomplete-endAdornment {
      top: calc(50% - 10px);
    }
  `}
`

// Trim the typed text when filtering so padded input still matches (e.g. the "Create X" option)
const creatableFilterOptions = createFilterOptions<any>({ trim: true })

const useStyles = makeStyles(theme => ({
  formControl: {
    position: 'relative',
    width: '100%',
  },
  helperText: {
    padding: '0 10px',
  },
}))

const PagAutocomplete = (props: any) => {
  const {
    className, // For Form Control
    error,
    optionLabelKey,
    optionValueKey,
    name,
    value,
    options,
    loading,
    isCreatable,
    AutocompleteProps,
    InputProps,
  } = props

  const classes = useStyles()
  const [newOption, setNewOption] = useState(null as any)
  const [selectedOption, setSelectedOption] = useState(null as any)
  const [selectedOptions, setSelectedOptions] = useState([] as any)

  useEffect(() => {
    const multiple = Boolean(AutocompleteProps?.multiple)
    if (!value) {
      if (!multiple) {
        setSelectedOption(null)
      } else {
        setSelectedOptions([])
      }
    } else {
      if (!multiple) {
        const temp =
          options.find((o: any) =>
            typeof value === 'object'
              ? o[optionValueKey] === value[optionValueKey]
              : o[optionValueKey] === value
          ) || null
        setSelectedOption(temp)
      } else {
        const temp = options.filter((o: any) => {
          if (!o) return false
          const temp = (value || []).find((v: any) => {
            if (!v) return false
            return typeof v === 'object'
              ? o[optionValueKey] === v[optionValueKey]
              : o[optionValueKey] === v
          })
          return Boolean(temp)
        })
        setSelectedOptions(temp)
      }
    }
    return () => {}
  }, [value, options, optionValueKey, AutocompleteProps?.multiple])

  const renderAutocomplete = useMemo(() => {
    const multiple = Boolean(AutocompleteProps?.multiple)
    return (
      <StyledAutocomplete
        fullWidth
        value={multiple ? selectedOptions : selectedOption}
        options={newOption ? [newOption, ...options] : options || []}
        onInputChange={(event: object, value: string, reason: string) => {
          if (!isCreatable || reason !== 'input') return
          const trimmed = (value || '').trim()
          const getLabel = (o: any) => {
            const label = AutocompleteProps?.getOptionLabel
              ? AutocompleteProps.getOptionLabel(o)
              : o?.[optionLabelKey]
            return label === null || label === undefined ? '' : `${label}`
          }
          const exists = (options || []).some(
            (o: any) => o && getLabel(o).trim().toLowerCase() === trimmed.toLowerCase()
          )
          if (!trimmed || exists) setNewOption(null)
          else
            setNewOption({
              [`${optionLabelKey}`]: `Create ${trimmed}`,
              newValue: trimmed,
              isNew: true,
            })
        }}
        onClose={(event: object, reason: string) => {
          if (isCreatable && Boolean(newOption)) setNewOption(null)
        }}
        {...(isCreatable ? { filterOptions: creatableFilterOptions } : {})}
        {...AutocompleteProps}
        renderInput={(params: any) => {
          const extra = { ...(InputProps || {}) }
          const userSlot = extra.InputProps
          delete extra.InputProps
          return (
            <TextField
              {...params}
              {...extra}
              InputProps={{
                ...params.InputProps,
                ...(userSlot || {}),
              }}
            />
          )
        }}
      />
    )
  }, [
    newOption,
    selectedOption,
    selectedOptions,
    options,
    isCreatable,
    AutocompleteProps,
    InputProps,
    optionLabelKey,
  ])

  return (
    <FormControl className={clsx(classes.formControl, className)} error={error ? true : false}>
      {renderAutocomplete}
      {error ? (
        <FormHelperText className={classes.helperText}>
          <b>{error.message}</b>
        </FormHelperText>
      ) : null}
      {loading && <PagCircularIndeterminate size={14} />}
    </FormControl>
  )
}

PagAutocomplete.defaultProps = {
  InputLabelProps: {},
  SelectProps: {},
  error: null,
}

export default PagAutocomplete
